import {
  getBusinessAnswer,
  getScopingToggle,
  isPerBusinessToggle,
  type BusinessAnswer,
  type BusinessScoped,
} from "@shared/businessScope";

type DisplayQuestion = {
  _id?: unknown;
  type?: string | null;
  parentQuestion?: unknown;
  businessScope?: string | null;
};

type DisplayResponse = {
  questionData?: DisplayQuestion | null;
  response?: (string | null | undefined)[] | null;
  byBusiness?: (BusinessAnswer | null | undefined)[] | null;
};

/** Whether a question's answer is stored per business on this submission. */
export const isBusinessScopedAnswer = (
  question: DisplayQuestion | null | undefined,
  responses: readonly (DisplayResponse | null | undefined)[],
  businesses: readonly BusinessScoped[],
): boolean =>
  businesses.length > 0 &&
  (isPerBusinessToggle(question) || !!getScopingToggle(question, responses));

/**
 * "Yes (A, B)" for a per-business toggle, "A: answer · B: answer" for its
 * follow-ups; null for questions answered once for the whole submission.
 */
export function formatBusinessScopedAnswer(
  question: DisplayQuestion | null | undefined,
  response: DisplayResponse | null | undefined,
  responses: readonly (DisplayResponse | null | undefined)[],
  businesses: readonly BusinessScoped[],
  formatOne: (answer: string[]) => string,
): string | null {
  if (!isBusinessScopedAnswer(question, responses, businesses)) return null;
  const nameOf = (id: string) =>
    businesses.find(b => b.operatorId === id)?.businessName || id;
  const entries = (response?.byBusiness ?? []).filter(
    (b): b is BusinessAnswer => !!b?.operatorId,
  );
  if (isPerBusinessToggle(question)) {
    return entries.length
      ? `Yes (${entries.map(b => nameOf(b.operatorId)).join(", ")})`
      : "No";
  }
  return (
    entries
      .map(
        b =>
          `${nameOf(b.operatorId)}: ${formatOne(getBusinessAnswer(response, b.operatorId))}`,
      )
      .join(" · ") || "—"
  );
}
